import React, {useEffect, useState} from 'react';
import {Box, Typography} from '@mui/material';
import Withdrawal from '../../RecommendsWithdrawal/Withdrawal';
import {Transfer} from '../../RecommendsTransfer/Transfer';

import SetUpNew from '../../RecommendsSetUpNew/SetUpNew';
import {ProtectionPlan} from '../../RecommendsSetUpNew/ProtectionPlan';
import {AnnuityPlan} from '../../RecommendsSetUpNew/AnnuityPlan';
import {POTS_ANNUITY, POTS_PROTECTION} from '../../../../utils/potsFilter';
import Contribute from '../../RecommendsContribute/Contribute';
import RecommendParserStep from '../../RecommendParserStep/RecommendParserStep';
import {useAccountContext} from '../../../../contexts/AccountContext';
import {OVRecommendActionEnum} from '../../../../schema-types/globalTypes';
import {recommendFieldsType} from '../recommendFields';
import {PlanLabelType, RecommendProps} from './validators';
import {RecommendsAction} from '../../RecommendsAction/RecommendsAction';
import {RecommendModify} from '../../RecommendModify/RecommendModify';
import {setContactnameFromGoals} from './utils';
import {useRecommendContext} from '../CreateRecommendHelpers';
import {reccNonEditable} from './CreateRecommendFooter';
import {
  RequirementPots_destinations,
  RequirementPots_sources,
} from '../../../../schema-types/RequirementPots';
import {useAutoForm} from '../../../../components/AutoForm/useAutoForm';
import RecommendAdHocFee from '../../RecommendAdHocFee/RecommendAdHocFee';

export const PLAN_LABELS: Record<string, PlanLabelType> = {
  withdraw: {
    first: 'From',
    second: 'Send funds to',
  },
  contribute: {
    first: 'Take from',
    second: 'Give to',
  },
  transfer: {
    first: 'From',
    second: 'To',
  },
  setUpNew: {
    first: 'Open',
  },
  modify: {
    first: 'Modifying',
  },
  close: {
    first: 'Closing',
    second: 'Send remaining funds to',
  },
  adHocFee: {
    first: 'Charge Ad-hoc Fee to',
  },
};

const PlanTypeContainer: React.FC<{
  selectionError: boolean;
  destinationPots: RequirementPots_destinations[];
  sourcePots: RequirementPots_sources[];
}> = ({selectionError, destinationPots, sourcePots}) => {
  const {values} = useAutoForm<recommendFieldsType>();
  const [contactName, setContactName] = useState<string>('');
  const {account, accountId} = useAccountContext();
  const {recommendDetails, recommendReviewId, isSPPDA} = useRecommendContext();

  useEffect(() => {
    if (account) {
      setContactnameFromGoals({
        values,
        account,
        setContactName,
      });
    }
  }, [account, values.contact]);

  if (!values.planAction) {
    return <RecommendParserStep errorText="Please complete the above" />;
  }

  if (selectionError) {
    return (
      <Box
        justifyContent="center"
        alignContent="center"
        width="100%"
        height={300}
        display="flex"
      >
        <Typography variant="h3">
          Sorry you cannot select this combination of options together.
        </Typography>
      </Box>
    );
  }

  const sourceDestinationLabels: PlanLabelType =
    values.planAction && values.planAction in PLAN_LABELS
      ? {...PLAN_LABELS[values.planAction]}
      : {};

  // new plan never needs sources and contribute coming from new plan also doesn't need sources
  if (
    sourcePots?.length === 0 &&
    values.planAction !== OVRecommendActionEnum.SET_UP_NEW &&
    values.planAction !== OVRecommendActionEnum.CONTRIBUTE &&
    values.planAction !== OVRecommendActionEnum.TRANSFER &&
    values.forcedMainPlanType
  ) {
    return <RecommendParserStep errorText="Please complete the above" />;
  }

  const props: RecommendProps = {
    contactName,
    sourcePots: reccNonEditable(recommendDetails)
      ? recommendDetails?.pots
          ?.filter((x) => x.planType === 'source')
          .map((x) => x.pot)
          .filter((p): p is NonNullable<typeof p> => p !== null) || []
      : sourcePots,
    destinationPots: reccNonEditable(recommendDetails)
      ? recommendDetails?.pots
          ?.filter((x) =>
            values.planAction === OVRecommendActionEnum.CONTRIBUTE
              ? true
              : x.planType === 'destination'
          )
          .map((x) => x.pot)
          .filter((p): p is NonNullable<typeof p> => p !== null) || []
      : destinationPots,
    sourceDestinationLabels,
  };

  const error = (recommendDetails && recommendDetails.action === undefined) || false;

  if (error) {
    <RecommendParserStep
      errorText={`Sorry, something went wrong when previously saving this ${
        isSPPDA ? 'instruction' : 'recommendation'
      }`}
    />;
  }

  if (values.planAction === OVRecommendActionEnum.SET_UP_NEW) {
    if (values.mainPlanType === POTS_PROTECTION) {
      return <ProtectionPlan {...props} />;
    }

    if (values.mainPlanType === POTS_ANNUITY) {
      return <AnnuityPlan {...props} />;
    }

    return <SetUpNew {...props} />;
  }

  if (values.planAction === OVRecommendActionEnum.TRANSFER) {
    return <Transfer {...props} />;
  }

  if (values.planAction === OVRecommendActionEnum.CONTRIBUTE) {
    return <Contribute {...props} />;
  }

  if (values.planAction === OVRecommendActionEnum.WITHDRAW) {
    return <Withdrawal {...props} />;
  }

  if (values.planAction === OVRecommendActionEnum.MODIFY) {
    return <RecommendModify {...props} />;
  }

  if (values.planAction === OVRecommendActionEnum.ACTION) {
    return <RecommendsAction {...props} />;
  }

  if (values.planAction === OVRecommendActionEnum.AD_HOC_FEE) {
    return <RecommendAdHocFee {...props} />;
  }

  return null;
};

export default PlanTypeContainer;
